import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createRating, fetchRating } from "../lib/api";

type Category = "energy" | "soda" | "sparkling";

const PRESET_VIBE_TAGS = [
	{ label: "Spicy", icon: "local_fire_department" },
	{ label: "Smooth", icon: "water_drop" },
	{ label: "Focus", icon: "psychology" },
	{ label: "Fruity", icon: "cruelty_free" },
];

export function AddRatingPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const isEditing = !!id;

	const { data: existingRating } = useQuery({
		queryKey: ["ratings", id],
		queryFn: () => fetchRating(id!),
		enabled: isEditing,
	});

	const [drinkName, setDrinkName] = useState("");
	const [brand, setBrand] = useState("");
	const [category, setCategory] = useState<Category | "">("");
	const [energyLevel, setEnergyLevel] = useState(5.0);
	const [flavorScore, setFlavorScore] = useState(3);
	const [vibeTags, setVibeTags] = useState<string[]>([]);
	const [tastingNotes, setTastingNotes] = useState("");
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [showCustomTagInput, setShowCustomTagInput] = useState(false);
	const [customTagValue, setCustomTagValue] = useState("");

	useEffect(() => {
		if (existingRating) {
			setDrinkName(existingRating.drink_name);
			setBrand(existingRating.brand || "");
			setCategory(existingRating.category);
			setEnergyLevel(existingRating.energy_level);
			setFlavorScore(existingRating.flavor_score);
			setVibeTags(existingRating.vibe_tags);
			setTastingNotes(existingRating.tasting_notes || "");
		}
	}, [existingRating]);

	const mutation = useMutation({
		mutationFn: createRating,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["ratings"] });
			queryClient.invalidateQueries({ queryKey: ["stats"] });
			navigate("/");
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const newErrors: Record<string, string> = {};

		if (!drinkName.trim()) newErrors.drinkName = "Drink name is required";
		if (!category) newErrors.category = "Category is required";
		if (vibeTags.length === 0)
			newErrors.vibeTags = "Select at least one vibe tag";

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setErrors({});
		mutation.mutate({
			drink_name: drinkName.trim(),
			brand: brand.trim() || undefined,
			category: category as Category,
			energy_level: energyLevel,
			flavor_score: flavorScore,
			vibe_tags: vibeTags,
			tasting_notes: tastingNotes.trim() || undefined,
		});
	};

	const toggleVibeTag = (tag: string) => {
		setVibeTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
		);
	};

	const addCustomTag = () => {
		const trimmed = customTagValue.trim();
		if (trimmed && !vibeTags.includes(trimmed)) {
			setVibeTags((prev) => [...prev, trimmed]);
		}
		setCustomTagValue("");
		setShowCustomTagInput(false);
	};

	return (
		<div className="min-h-screen bg-surface">
			<header className="fixed top-0 right-0 left-0 h-16 md:h-20 bg-brand-white border-b-4 border-brand-black z-40 flex justify-between items-center px-4 md:px-8">
				<h1 className="text-xl md:text-3xl font-black tracking-tighter text-brand-black uppercase font-[Space_Grotesk]">
					{isEditing ? "EDIT RATING" : "NEW RATING"}
				</h1>
				<button
					onClick={() => navigate(-1)}
					className="w-12 h-12 flex items-center justify-center border-[3px] border-brand-black hover:bg-secondary-container transition-colors bg-brand-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
				>
					<span className="material-symbols-outlined">close</span>
				</button>
			</header>

			<div className="pt-32 px-4 md:px-8 pb-32 max-w-4xl mx-auto w-full">
				<form onSubmit={handleSubmit} className="space-y-8">
					<div className="bg-brand-white border-4 border-brand-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
						<div className="space-y-8">
							<div>
								<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-2">
									Drink Name
								</label>
								<input
									className={`w-full border-[3px] border-brand-black p-4 font-body-lg text-[18px] leading-[1.5] font-medium bg-surface-container-lowest transition-all placeholder:text-outline-variant focus:border-primary-container focus:shadow-[4px_4px_0px_0px_#c1f100] outline-none ${
										errors.drinkName ? "border-error" : ""
									}`}
									placeholder="e.g. Midnight Surge"
									value={drinkName}
									onChange={(e) => setDrinkName(e.target.value)}
								/>
								{errors.drinkName && (
									<p className="font-body-md text-[16px] leading-[1.5] text-error mt-1">
										{errors.drinkName}
									</p>
								)}
							</div>
							<div>
								<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-2">
									Brand
								</label>
								<input
									className="w-full border-[3px] border-brand-black p-4 font-body-lg text-[18px] leading-[1.5] font-medium bg-surface-container-lowest transition-all placeholder:text-outline-variant focus:border-primary-container focus:shadow-[4px_4px_0px_0px_#c1f100] outline-none"
									placeholder="e.g. Volt Cola"
									value={brand}
									onChange={(e) => setBrand(e.target.value)}
								/>
							</div>
							<div>
								<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-2">
									Category
								</label>
								<div className="flex gap-4">
									{(["energy", "soda", "sparkling"] as const).map((c) => (
										<button
											key={c}
											type="button"
											onClick={() => setCategory(c)}
											className={`flex-1 py-3 border-[3px] border-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase transition-all ${
												category === c
													? "bg-primary-container text-on-primary shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] -translate-x-1 -translate-y-1"
													: "bg-surface-container-lowest text-on-surface hover:bg-secondary-container"
											}`}
										>
											{c}
										</button>
									))}
								</div>
								{errors.category && (
									<p className="font-body-md text-[16px] leading-[1.5] text-error mt-1">
										{errors.category}
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="bg-brand-white border-4 border-brand-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
						<div className="flex justify-between items-center mb-6">
							<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase">
								Energy Level
							</label>
							<span className="font-headline-md text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-primary-container bg-primary-container/10 px-3 py-1 border-2 border-brand-black">
								{energyLevel}
							</span>
						</div>
						<div className="relative py-4">
							<input
								className="w-full accent-brand-green h-3 cursor-pointer"
								max="10"
								min="0"
								step="0.5"
								type="range"
								value={energyLevel}
								onChange={(e) => setEnergyLevel(parseFloat(e.target.value))}
							/>
							<div className="flex justify-between mt-4 font-body-md text-[16px] leading-[1.5] font-bold text-outline uppercase tracking-wider">
								<span>Snooze</span>
								<span>Voltage</span>
							</div>
						</div>
					</div>

					<div className="bg-brand-white border-4 border-brand-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
						<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-6">
							Flavor Score
						</label>
						<div className="flex gap-4">
							{[1, 2, 3, 4, 5].map((star) => (
								<button
									key={star}
									type="button"
									onClick={() => setFlavorScore(star)}
									className="group"
								>
									<span
										className={`material-symbols-outlined text-[48px] ${
											star <= flavorScore
												? "text-brand-yellow"
												: "text-surface-variant"
										} group-hover:-translate-y-1 transition-transform`}
										style={{
											fontVariationSettings: `'FILL' ${star <= flavorScore ? 1 : 0}`,
											WebkitTextStroke: "2px black",
										}}
									>
										star
									</span>
								</button>
							))}
						</div>
					</div>

					<div className="bg-brand-white border-4 border-brand-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
						<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-6">
							Vibe Tags
						</label>
						<div className="flex flex-wrap gap-4">
							{PRESET_VIBE_TAGS.map((tag) => {
								const isSelected = vibeTags.includes(tag.label);
								return (
									<button
										key={tag.label}
										type="button"
										onClick={() => toggleVibeTag(tag.label)}
										className={`px-6 py-3 border-[3px] border-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase transition-all flex items-center gap-2 ${
											isSelected
												? "bg-secondary-container text-brand-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
												: "bg-surface-container-low text-on-surface hover:bg-primary-container hover:text-brand-white"
										}`}
									>
										<span className="material-symbols-outlined">
											{tag.icon}
										</span>
										{tag.label}
									</button>
								);
							})}
							{showCustomTagInput ? (
								<div className="flex items-center gap-2">
									<input
										className="border-[3px] border-brand-black p-2 font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase bg-surface-container-low focus:border-primary-container focus:shadow-[4px_4px_0px_0px_#c1f100] outline-none"
										placeholder="Custom tag..."
										value={customTagValue}
										onChange={(e) => setCustomTagValue(e.target.value)}
										onKeyDown={(e) =>
											e.key === "Enter" && (e.preventDefault(), addCustomTag())
										}
										autoFocus
									/>
									<button
										type="button"
										onClick={addCustomTag}
										className="px-3 py-2 border-[3px] border-brand-black bg-brand-green font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase"
									>
										ADD
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={() => setShowCustomTagInput(true)}
									className="px-6 py-3 border-[3px] border-dashed border-brand-black bg-surface-container-low text-outline font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase hover:border-solid hover:text-on-surface transition-all flex items-center gap-2"
								>
									<span className="material-symbols-outlined">add</span>
									Custom
								</button>
							)}
						</div>
						{errors.vibeTags && (
							<p className="font-body-md text-[16px] leading-[1.5] text-error mt-3">
								{errors.vibeTags}
							</p>
						)}
						{vibeTags.length > 0 && (
							<div className="flex flex-wrap gap-2 mt-4">
								{vibeTags.map((tag) => (
									<span
										key={tag}
										className="px-3 py-1.5 border-2 border-brand-black bg-secondary-container text-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase flex items-center gap-1"
									>
										{tag}
										<button
											type="button"
											onClick={() => toggleVibeTag(tag)}
											className="ml-1"
										>
											<span className="material-symbols-outlined text-sm">
												close
											</span>
										</button>
									</span>
								))}
							</div>
						)}
					</div>

					<div className="bg-brand-white border-4 border-brand-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
						<label className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface uppercase block mb-2">
							Tasting Notes
						</label>
						<textarea
							className="w-full border-[3px] border-brand-black p-4 font-body-md text-[16px] leading-[1.5] bg-surface-container-lowest transition-all placeholder:text-outline-variant focus:border-primary-container focus:shadow-[4px_4px_0px_0px_#c1f100] outline-none resize-none h-24"
							placeholder="e.g. Intense citrus blend with a smooth finish..."
							maxLength={200}
							value={tastingNotes}
							onChange={(e) => setTastingNotes(e.target.value)}
						/>
						<p className="font-body-md text-[16px] leading-[1.5] text-outline text-right mt-1">
							{tastingNotes.length}/200
						</p>
					</div>

					<div className="pt-8 flex justify-end">
						<button
							type="submit"
							disabled={mutation.isPending}
							className="w-full md:w-auto px-12 py-6 bg-brand-green border-4 border-brand-black font-headline-lg text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] uppercase text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all hover:bg-brand-green/90 flex justify-center items-center gap-4"
						>
							<span className="material-symbols-outlined text-3xl">save</span>
							{mutation.isPending ? "SAVING..." : "SAVE SIP"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
